import { usePersonalView } from "../lib/usePersonalView";
import { useViewSearch } from "../lib/useViewSearch";
import PersonalViewNotice, { PersonalViewReset } from "../components/PersonalViewNotice";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import {
  addCostCenterMember,
  clearCostCenterDisplayName,
  createManualCostCenterEntity,
  fetchCostCenterManualEvents,
  fetchCostCenterSourceCoverage,
  fetchCostCenters,
  mergeCostCenterAlias,
  removeCostCenterMember,
  setCostCenterDisplayName,
  undoCostCenterAlias,
} from "../api/costCenters";
import type {
  CostCenterEntity,
  CostCenterAnchorRawReference,
  CostCenterManualEvent,
  CostCenterSourceCoverage,
  CostCenterType,
  ManualCostCenterEntityType,
  ManualProjectRole,
} from "../api/costCenters";
import CostCenterEditDrawer, {
  type EditableCostCenter,
} from "../components/CostCenterEditDrawer";
import {
  TableEmptyRow,
  TableErrorRow,
  TableHeadCell,
  TableLoadingRow,
} from "../components/DataTable";
import FilterDropdown from "../components/FilterDropdown";
import SearchInput from "../components/SearchInput";
import {
  costCenterHref,
  costCenterTypeLabel,
  displayCostCenterMeta,
  displayCostCenterRawReference,
  displayCostCenterTitle,
} from "../lib/costCenterDisplay";
import {
  coverageRowToCostCenterEntity,
  uniqueCostCenterEntities,
} from "../lib/costCenterCoverageEntity";
import { formatDateShort, formatEuro, formatQty } from "../lib/format";
import { uiApiErrorMessage } from "../lib/apiErrorLabels";
import { useDebouncedValue } from "../lib/useDebouncedValue";
import { useEscapeKey } from "../lib/useEscapeKey";
import { isDemoMode } from "../lib/env";
import SectionInfo from "../components/SectionInfo";

const PAGE_SIZE = 50;
const ACTIVITY_EVENT_LIMIT = 500;

type TypeFilter = "all" | CostCenterType;
type CostCenterTab = TypeFilter;
type TimeWindow = "all" | "last_year" | "ytd" | "last30";
type CustomerReferenceView = "grouped" | "raw";
type CreditSpendField = keyof Pick<
  CostCenterEntity,
  "credit_spend" | "credit_spend_last_year"
>;
type ManualEventsPanelQuery = {
  isLoading: boolean;
  isError: boolean;
  isFetching: boolean;
  data?: { items: CostCenterManualEvent[] };
};

type WindowConfig = {
  value: TimeWindow;
  label: string;
  spendField: keyof Pick<
    CostCenterEntity,
    "total_spend" | "spend_ytd" | "spend_last_year" | "spend_last30"
  >;
  creditField?: CreditSpendField;
  spendLabel: string;
};

const TYPE_FILTERS: { value: TypeFilter; label: string }[] = [
  { value: "all", label: "Alle" },
  { value: "project", label: "Projekte" },
  { value: "delivery_address", label: "Adressen" },
  { value: "customer_reference", label: "Kunden" },
  { value: "order_context", label: "Aufträge" },
  { value: "manual_cost_center", label: "Manuell" },
];

// User-visible tab values: order_context and manual_cost_center are out of
// the navigation; manual relationship creation lives in the activity pill,
// and order_context cost centers are hidden until the data path is revived.
const VISIBLE_TYPE_TABS: ReadonlySet<TypeFilter> = new Set([
  "all",
  "project",
  "delivery_address",
  "customer_reference",
]);

const COST_CENTER_TABS: { value: CostCenterTab; label: string }[] = [
  ...TYPE_FILTERS.filter((filter) => VISIBLE_TYPE_TABS.has(filter.value)),
];

const TIME_WINDOWS: WindowConfig[] = [
  {
    value: "all",
    label: "Gesamt",
    spendField: "total_spend",
    creditField: "credit_spend",
    spendLabel: "Ausgaben gesamt",
  },
  {
    value: "last_year",
    label: "Letzte 12 M.",
    spendField: "spend_last_year",
    creditField: "credit_spend_last_year",
    spendLabel: "Ausgaben 12 M.",
  },
  {
    value: "ytd",
    label: "YTD",
    spendField: "spend_ytd",
    spendLabel: "Ausgaben YTD",
  },
  {
    value: "last30",
    label: "Letzte 30 T.",
    spendField: "spend_last30",
    spendLabel: "Ausgaben 30 T.",
  },
];

const MANUAL_CREATE_MEMBER_TYPES = new Set([
  "project",
  "delivery_address",
  "customer_reference",
]);

function manualCreateButtonLabel(): string {
  return "Projekt anlegen";
}

function manualCreateNamePlaceholder(
  projectRole: ManualProjectRole,
): string {
  return projectRole === "project"
    ? "z. B. ÖWG Weiz Haus 35"
    : "z. B. ÖWG Gesamt";
}

export default function CostCentersPage() {
  const queryClient = useQueryClient();
  const personalView = usePersonalView("cost_centers");
  const searchParams = personalView.params;
  const costCenterTab = costCenterTabFromParam(searchParams.get("type"));
  const timeWindow = (TIME_WINDOWS.some(w => w.value === searchParams.get("window")) ? searchParams.get("window") : "last_year") as TimeWindow;
  const customerReferenceView: CustomerReferenceView = searchParams.get("customer_view") === "raw" ? "raw" : "grouped";
  function update(key: string, value: string, defaultValue: string) {
    const next = new URLSearchParams(searchParams);
    if (value === defaultValue) next.delete(key); else next.set(key, value);
    personalView.change(next);
  }
  const setTimeWindow = (v: TimeWindow) => update("window", v, "last_year");
  const setCustomerReferenceView = (v: CustomerReferenceView) => update("customer_view", v, "grouped");
  const [editingCostCenter, setEditingCostCenter] =
    useState<EditableCostCenter | null>(null);
  const [showActivityDrawer, setShowActivityDrawer] = useState(false);
  const [showManualCreateDrawer, setShowManualCreateDrawer] = useState(false);
  const [showManualGroupingDrawer, setShowManualGroupingDrawer] =
    useState(false);
  const { search, setSearch, searchPending } = useViewSearch(personalView);
  const debouncedSearch = searchParams.get("q") ?? "";
  const typeFilter: TypeFilter = costCenterTab;
  const isCustomerAnchorView =
    typeFilter === "customer_reference" && customerReferenceView === "grouped";
  const coverageView: "customer_anchor" | "raw" = isCustomerAnchorView
    ? "customer_anchor"
    : "raw";

  useEffect(() => {
    const raw = searchParams.get("type");
    if (!raw || raw === costCenterTab) return;
    const next = new URLSearchParams(searchParams);
    next.delete("type"); next.set("view", "default");
    personalView.setUrl(next, { replace: true });
  }, [searchParams, costCenterTab, personalView.setUrl]);

  function setCostCenterTab(next: CostCenterTab) { update("type", next, "all"); }

  const coverageBaseParams = {
    type: typeFilter === "all" ? undefined : typeFilter,
    q: debouncedSearch || undefined,
    view: coverageView,
  };
  const coverageRowsQuery = useQuery({
    queryKey: ["cost-center-source-coverage", "rows", coverageBaseParams],
    queryFn: () =>
      fetchCostCenterSourceCoverage({
        ...coverageBaseParams,
        status: "all",
        limit: PAGE_SIZE,
      }),
    enabled: personalView.ready,
    staleTime: 30_000,
  });
  const manualEventsQuery = useQuery({
    queryKey: ["cost-center-manual-events"],
    queryFn: () => fetchCostCenterManualEvents({ limit: ACTIVITY_EVENT_LIMIT }),
    enabled: showActivityDrawer,
    staleTime: 30_000,
  });
  const hasLoadedEntitiesOnce = useRef(false);
  useEffect(() => {
    if (coverageRowsQuery.isSuccess) {
      hasLoadedEntitiesOnce.current = true;
    }
  }, [coverageRowsQuery.isSuccess]);

  const refreshing = !personalView.ready || searchPending || coverageRowsQuery.isFetching;
  const coverageRows = refreshing
    ? []
    : (coverageRowsQuery.data?.items ?? []);
  const createButtonLabel = manualCreateButtonLabel();

  const showTypeColumn = typeFilter === "all";

  function openManualCreateDrawer() {
    setShowManualCreateDrawer(true);
  }

  return (
    <main className="mx-auto max-w-7xl px-6 py-6">
      <header className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">
            Kostenstellen
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <PersonalViewReset view={personalView} defaultLabel="Alle Kostenstellen, letzte 12 Monate, Kunden gruppiert." />
          {!isDemoMode() && (
            <ActivityPill onClick={() => setShowActivityDrawer(true)} />
          )}
        </div>
      </header>

      <PersonalViewNotice view={personalView} defaultLabel="Alle Kostenstellen, letzte 12 Monate, Kunden gruppiert." />
      {/* Keine Kennzahlkarten mehr (#630); Summen je Kostenstelle stehen in der Tabelle. */}

      <TypeTabBar value={costCenterTab} params={searchParams} onChange={setCostCenterTab} />
      <div className="mb-4 mt-4 flex flex-wrap items-center gap-3">
        <SearchInput className="w-full basis-full sm:w-auto sm:basis-auto" disabled={!personalView.ready}
          value={search}
          onChange={setSearch}
          placeholder="Name, Adresse, Referenz…"
          ariaLabel="Kostenstelle suchen"
        />
        <TimeWindowTabs
          value={timeWindow}
          onChange={setTimeWindow}
        />
        {typeFilter === "customer_reference" ? (
          <CustomerReferenceViewTabs
            value={customerReferenceView}
            onChange={setCustomerReferenceView}
          />
        ) : null}
        {!isDemoMode() && (
          <button
            type="button"
            onClick={() => openManualCreateDrawer()}
            className="ml-auto inline-flex h-9 items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 px-3 text-sm font-medium text-emerald-800 hover:bg-emerald-100"
          >
            <span aria-hidden="true" className="text-base leading-none">+</span>
            {createButtonLabel}
          </button>
        )}
      </div>
      <CoverageEntityTable
        query={{
          isLoading: coverageRowsQuery.isLoading,
          isError: coverageRowsQuery.isError,
          isFetching: refreshing,
        }}
        rows={coverageRows}
        loadingLabel={typeFilterLoadingLabel(typeFilter)}
        showTypeColumn={showTypeColumn}
        isCustomerAnchorView={isCustomerAnchorView}
        hasLoadedOnce={hasLoadedEntitiesOnce.current}
        onEdit={(row) =>
          setEditingCostCenter({
            costCenterType: row.entity_type,
            costCenterKey: row.entity_key,
            displayLabel: row.display_label,
            sourceDisplayLabel: row.source_display_label ?? null,
            manualDisplayLabel: row.manual_display_label ?? null,
            customerAnchorLabel: row.customer_anchor_label ?? null,
            projectRole: row.project_role ?? null,
          })
        }
      />
      <CostCenterEditDrawer
        entity={editingCostCenter}
        onClose={() => setEditingCostCenter(null)}
      />
      <ActivityDrawer
        open={showActivityDrawer}
        onClose={() => setShowActivityDrawer(false)}
        eventsQuery={manualEventsQuery}
        onOpenManualGrouping={() => {
          setShowActivityDrawer(false);
          setShowManualGroupingDrawer(true);
        }}
        onJump={(event) => {
          if (!event.cost_center_type || !event.cost_center_key) return;
          setShowActivityDrawer(false);
          setEditingCostCenter({
            costCenterType: event.cost_center_type,
            costCenterKey: event.cost_center_key,
            displayLabel: event.display_label,
            sourceDisplayLabel: event.source_display_label ?? null,
            manualDisplayLabel: event.manual_display_label ?? null,
            customerAnchorLabel: null,
            projectRole: null,
          });
        }}
      />
      <ManualGroupingDrawer
        open={showManualGroupingDrawer}
        onClose={() => setShowManualGroupingDrawer(false)}
        onCreated={() => {
          void queryClient.invalidateQueries({
            queryKey: ["cost-center-relationships"],
          });
          void queryClient.invalidateQueries({
            queryKey: ["cost-center-source-coverage"],
          });
          void queryClient.invalidateQueries({
            queryKey: ["cost-center-manual-events"],
          });
          void queryClient.invalidateQueries({
            queryKey: ["cost-center-assignments"],
          });
        }}
      />
      <ManualCostCenterCreateDrawer
        open={showManualCreateDrawer}
        onClose={() => setShowManualCreateDrawer(false)}
        onCreated={(item) => {
          setShowManualCreateDrawer(false);
          setCostCenterTab("project");
          setSearch(
            displayCostCenterTitle(
              item.cost_center_type,
              item.cost_center_key,
              item.display_label,
              {
                customerAnchorLabel: item.customer_anchor_label,
                manualLabel: item.manual_display_label,
              },
            ),
          );
          setEditingCostCenter({
            costCenterType: item.cost_center_type,
            costCenterKey: item.cost_center_key,
            displayLabel: item.display_label,
            sourceDisplayLabel: item.source_display_label ?? item.display_label,
            manualDisplayLabel: item.manual_display_label ?? null,
            customerAnchorLabel: item.customer_anchor_label ?? null,
            projectRole: item.project_role ?? null,
          });
          void queryClient.invalidateQueries({
            queryKey: ["cost-centers"],
          });
          void queryClient.invalidateQueries({
            queryKey: ["cost-center-source-coverage"],
          });
          void queryClient.invalidateQueries({
            queryKey: ["cost-center-manual-events"],
          });
        }}
      />
    </main>
  );
}

function ActivityPill({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex h-9 items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50"
      title="Letzte manuelle Änderungen und manuelle Zuordnung anlegen"
    >
      <ActivityIcon />
      Aktivität
    </button>
  );
}

function ActivityIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    >
      <circle cx="12" cy="12" r="9" />
      <polyline points="12 7 12 12 15 14" />
    </svg>
  );
}

function ActivityDrawer({
  open,
  onClose,
  eventsQuery,
  onOpenManualGrouping,
  onJump,
}: {
  open: boolean;
  onClose: () => void;
  eventsQuery: ManualEventsPanelQuery;
  onOpenManualGrouping: () => void;
  onJump: (event: CostCenterManualEvent) => void;
}) {
  useEscapeKey(onClose, open);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50">
      <button
        type="button"
        className="absolute inset-0 cursor-default bg-slate-900/20"
        aria-label="Aktivität schließen"
        onClick={onClose}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby="cost-center-activity-title"
        className="absolute inset-y-0 right-0 flex w-[min(40rem,100vw)] flex-col border-l border-slate-200 bg-white shadow-2xl"
      >
        <header className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Kostenstellen-Aktivität
            </div>
            <div className="flex items-center gap-1.5">
              <h2
              id="cost-center-activity-title"
              className="mt-1 text-lg font-semibold text-slate-900"
            >
              Letzte manuelle Änderungen
            </h2>
              <SectionInfo label="Letzte manuelle Änderungen">
                Anzeigenamen, Zusammenführungen, zugeordnete Kostenstellen und manuell angelegte Kostenstellen.
              </SectionInfo>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="h-8 rounded-md px-2 text-sm text-slate-500 hover:bg-slate-100 hover:text-slate-900"
          >
            Schließen
          </button>
        </header>
        <div className="border-b border-slate-200 px-5 py-3">
          <button
            type="button"
            onClick={onOpenManualGrouping}
            className="inline-flex h-9 items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 px-3 text-sm font-medium text-emerald-800 hover:bg-emerald-100"
          >
            <span aria-hidden="true" className="text-base leading-none">+</span>
            Manuelle Zuordnung anlegen
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-auto px-5 py-4">
          <ManualChangesPanel query={eventsQuery} onJump={onJump} />
        </div>
      </aside>
    </div>
  );
}

function ManualCostCenterCreateDrawer({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (entity: CostCenterEntity) => void;
}) {
  useEscapeKey(onClose, open);
  if (!open) return null;
  const title = manualCreateButtonLabel();
  return (
    <div className="fixed inset-0 z-50">
      <button
        type="button"
        className="absolute inset-0 cursor-default bg-slate-900/20"
        aria-label={`${title} schließen`}
        onClick={onClose}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby="manual-cost-center-title"
        className="absolute inset-y-0 right-0 flex w-[min(34rem,100vw)] flex-col border-l border-slate-200 bg-white shadow-2xl"
      >
        <header className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Manuelles Projekt
            </div>
            <h2
              id="manual-cost-center-title"
              className="mt-1 text-lg font-semibold text-slate-900"
            >
              {title}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="h-8 rounded-md px-2 text-sm text-slate-500 hover:bg-slate-100 hover:text-slate-900"
          >
            Schließen
          </button>
        </header>
        <ManualCostCenterCreateForm onCreated={onCreated} />
      </aside>
    </div>
  );
}

function ManualCostCenterCreateForm({
  onCreated,
}: {
  onCreated: (entity: CostCenterEntity) => void;
}) {
  const costCenterType: ManualCostCenterEntityType = "project";
  const [displayName, setDisplayName] = useState("");
  const [projectRole, setProjectRole] = useState<ManualProjectRole>("project");
  const [memberSearch, setMemberSearch] = useState("");
  const [selectedMembers, setSelectedMembers] = useState<CostCenterEntity[]>([]);
  const [note, setNote] = useState("");

  const debouncedMemberSearch = useDebouncedValue(memberSearch, 200);
  const trimmedMemberSearch = debouncedMemberSearch.trim();
  const memberSearchIsSettling =
    memberSearch.trim().length >= 2 &&
    memberSearch.trim() !== trimmedMemberSearch;
  const memberSearchQuery = useQuery({
    queryKey: [
      "cost-centers",
      "manual-create-member-search",
      trimmedMemberSearch,
    ],
    queryFn: () =>
      fetchCostCenters({
        q: trimmedMemberSearch,
        limit: 10,
        sort: "name_asc",
      }),
    enabled: trimmedMemberSearch.length >= 2,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
  const memberCustomerReferenceQuery = useQuery({
    queryKey: [
      "cost-center-source-coverage",
      "manual-create-member-search",
      "customer-anchors",
      trimmedMemberSearch,
    ],
    queryFn: () =>
      fetchCostCenterSourceCoverage({
        type: "customer_reference",
        q: trimmedMemberSearch,
        view: "customer_anchor",
        limit: 10,
      }),
    enabled: trimmedMemberSearch.length >= 2,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
  const selectedMemberKeys = new Set(
    selectedMembers.map(
      (member) => `${member.cost_center_type}:${member.cost_center_key}`,
    ),
  );
  const memberOptions = uniqueCostCenterEntities([
    ...(memberCustomerReferenceQuery.data?.items ?? []).map(
      coverageRowToCostCenterEntity,
    ),
    ...(memberSearchQuery.data?.items ?? []).filter(
      (candidate) => candidate.cost_center_type !== "customer_reference",
    ),
  ]).filter((candidate) => {
    const key = `${candidate.cost_center_type}:${candidate.cost_center_key}`;
    if (!MANUAL_CREATE_MEMBER_TYPES.has(String(candidate.cost_center_type))) {
      return false;
    }
    if (
      candidate.cost_center_type === "project" &&
      candidate.project_role === "reporting_group"
    ) {
      return false;
    }
    if (selectedMemberKeys.has(key)) return false;
    return true;
  });
  const memberSearchLoading =
    memberSearch.trim().length >= 2 &&
    (memberSearchQuery.isLoading ||
      memberCustomerReferenceQuery.isLoading ||
      memberSearchIsSettling);
  const createMutation = useMutation({
    mutationFn: createManualCostCenterEntity,
    onSuccess: (result) => {
      onCreated(result.item);
      setDisplayName("");
      setProjectRole("project");
      setMemberSearch("");
      setSelectedMembers([]);
      setNote("");
    },
  });
  const canSubmit = displayName.trim().length > 0 && !createMutation.isPending;
  return (
    <div className="min-h-0 flex-1 overflow-auto px-5 py-4">
      <div className="space-y-4">
        <div>
          <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
            Rolle
          </div>
          <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
            <ProjectRoleOption
              value="project"
              selected={projectRole === "project"}
              disabled={createMutation.isPending}
              title="Projekt"
              description="Konkretes Projekt mit Kunde, Adresse oder verwandten Kostenstellen."
              onSelect={setProjectRole}
            />
            <ProjectRoleOption
              value="reporting_group"
              selected={projectRole === "reporting_group"}
              disabled={createMutation.isPending}
              title="Sammelprojekt"
              description="Zusätzliche Auswertung über bestehende Kostenstellen."
              onSelect={setProjectRole}
            />
          </div>
        </div>
        <label className="block text-xs font-medium uppercase tracking-wide text-slate-500">
          Name
          <input
            value={displayName}
            disabled={createMutation.isPending}
            onChange={(event) => setDisplayName(event.target.value)}
            placeholder={manualCreateNamePlaceholder(projectRole)}
            className="mt-1 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm font-normal normal-case tracking-normal text-slate-800 outline-none placeholder:text-slate-400 focus:border-emerald-300 focus:ring-2 focus:ring-emerald-500/20 disabled:bg-slate-50 disabled:text-slate-400"
            autoFocus
          />
        </label>
        <div>
          <label className="block text-xs font-medium uppercase tracking-wide text-slate-500">
            Kostenstellen direkt zuordnen
            <input
              value={memberSearch}
              disabled={createMutation.isPending}
              onChange={(event) => setMemberSearch(event.target.value)}
              placeholder="Kunde, Adresse oder Projekt suchen..."
              className="mt-1 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm font-normal normal-case tracking-normal text-slate-800 outline-none placeholder:text-slate-400 focus:border-emerald-300 focus:ring-2 focus:ring-emerald-500/20 disabled:bg-slate-50 disabled:text-slate-400"
            />
          </label>
          {selectedMembers.length > 0 ? (
            <div className="mt-2 flex flex-wrap gap-2">
              {selectedMembers.map((member) => {
                const title = manualEntityTitle(member);
                const rawReference = manualEntityRawReference(member);
                return (
                  <button
                    key={`${member.cost_center_type}:${member.cost_center_key}`}
                    type="button"
                    disabled={createMutation.isPending}
                    onClick={() =>
                      setSelectedMembers((current) =>
                        current.filter(
                          (item) =>
                            `${item.cost_center_type}:${item.cost_center_key}` !==
                            `${member.cost_center_type}:${member.cost_center_key}`,
                        ),
                      )
                    }
                    className="inline-flex max-w-full items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-900 hover:bg-emerald-100 disabled:opacity-60"
                    title="Zuordnung entfernen"
                  >
                    <span className="min-w-0 text-left">
                      <span className="block truncate">{title}</span>
                      {rawReference && rawReference !== title ? (
                        <span className="block truncate text-[11px] font-normal text-emerald-700">
                          Referenz: {rawReference}
                        </span>
                      ) : null}
                    </span>
                    <span aria-hidden="true">×</span>
                  </button>
                );
              })}
            </div>
          ) : null}
          {memberSearch.trim().length >= 2 ? (
            <div className="mt-2 h-56 overflow-y-auto rounded-md border border-slate-200">
              {memberSearchLoading && memberOptions.length === 0 ? (
                <div className="px-3 py-2 text-sm text-slate-500">Lädt...</div>
              ) : memberOptions.length > 0 ? (
                memberOptions.map((candidate) => {
                  const title = manualEntityTitle(candidate);
                  const rawReference = manualEntityRawReference(candidate);
                  return (
                    <button
                      key={`${candidate.cost_center_type}:${candidate.cost_center_key}`}
                      type="button"
                      disabled={createMutation.isPending}
                      onClick={() => {
                        setSelectedMembers((current) => [...current, candidate]);
                        setMemberSearch("");
                      }}
                      className="block w-full border-b border-slate-100 px-3 py-2 text-left last:border-b-0 hover:bg-slate-50 disabled:cursor-default disabled:opacity-60"
                    >
                      <div className="truncate text-sm font-medium text-slate-900">
                        {title}
                      </div>
                      <div className="mt-0.5 truncate text-xs text-slate-500">
                        {rawReference && rawReference !== title
                          ? `Referenz: ${rawReference}`
                          : costCenterTypeLabel(candidate.cost_center_type)}
                      </div>
                    </button>
                  );
                })
              ) : (
                <div className="px-3 py-2 text-sm text-slate-500">
                  Keine passende Kostenstelle gefunden.
                </div>
              )}
            </div>
          ) : null}
        </div>
        <label className="block text-xs font-medium uppercase tracking-wide text-slate-500">
          Notiz optional
          <input
            value={note}
            disabled={createMutation.isPending}
            onChange={(event) => setNote(event.target.value)}
            className="mt-1 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm font-normal normal-case tracking-normal text-slate-800 outline-none placeholder:text-slate-400 focus:border-emerald-300 focus:ring-2 focus:ring-emerald-500/20 disabled:bg-slate-50 disabled:text-slate-400"
          />
        </label>
        <div className="flex items-center gap-3">
          <button
            type="button"
            disabled={!canSubmit}
            onClick={() =>
              createMutation.mutate({
                costCenterType,
                displayName: displayName.trim(),
                projectRole,
                members: selectedMembers.map((member) => ({
                  costCenterType: member.cost_center_type,
                  costCenterKey: member.cost_center_key,
                })),
                note: note.trim() || undefined,
              })
            }
            className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-sm font-medium text-emerald-800 hover:bg-emerald-100 disabled:cursor-default disabled:border-slate-200 disabled:bg-white disabled:text-slate-400"
          >
            {createMutation.isPending ? "Speichert..." : "Anlegen"}
          </button>
          {createMutation.isError ? (
            <span className="text-sm text-rose-600">
              {uiApiErrorMessage(
                createMutation.error,
                "Kostenstelle konnte nicht angelegt werden.",
              )}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function ProjectRoleOption({
  value,
  selected,
  disabled,
  title,
  description,
  onSelect,
}: {
  value: ManualProjectRole;
  selected: boolean;
  disabled: boolean;
  title: string;
  description: string;
  onSelect: (value: ManualProjectRole) => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      disabled={disabled}
      onClick={() => onSelect(value)}
      className={[
        "rounded-md border px-3 py-2 text-left transition disabled:cursor-default disabled:opacity-60",
        selected
          ? "border-emerald-300 bg-emerald-50 text-emerald-950"
          : "border-slate-200 bg-white text-slate-800 hover:bg-slate-50",
      ].join(" ")}
    >
      <span className="block text-sm font-semibold">{title}</span>
      <span className="mt-0.5 block text-xs leading-5 text-slate-500">
        {description}
      </span>
    </button>
  );
}

function manualEventActorLabel(actorUserId: string | null): string {
  if (!actorUserId) return "unbekannt";
  return actorUserId;
}

function formatDateTimeShort(value: string | null | undefined): string {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return formatDateShort(value);
  return new Intl.DateTimeFormat("de-AT", {
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    month: "2-digit",
    timeZone: "Europe/Vienna",
    year: "2-digit",
  }).format(date);
}

function costCenterTabFromParam(value: string | null): CostCenterTab {
  if (!value) return "all";
  if (VISIBLE_TYPE_TABS.has(value as TypeFilter)) {
    return value as CostCenterTab;
  }
  return "all";
}

function TypeTabBar({
  value, params, onChange,
}: {
  value: CostCenterTab;
  params: URLSearchParams;
  onChange: (value: CostCenterTab) => void;
}) {
  return (
    <nav
      aria-label="Kostenstellenansicht"
      className="mb-4 border-b border-slate-200"
    >
      <ul className="-mb-px flex flex-wrap gap-x-1 text-sm">
        {COST_CENTER_TABS.map((filter) => {
          const isActive = value === filter.value;
          const target = new URLSearchParams(params);
          if (filter.value === "all") target.delete("type"); else target.set("type", filter.value);
          target.set("view", "default");
          return (
            <li key={filter.value}>
              <Link
                to={`/cost-centers?${target}`}
                onClick={event => { if (!event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && event.button === 0) { event.preventDefault(); onChange(filter.value); } }}
                aria-current={isActive ? "page" : undefined}
                className={[
                  "-mb-px inline-flex items-center border-b-2 px-3 py-2 font-medium transition-colors",
                  isActive
                    ? "border-emerald-600 text-emerald-700"
                    : "border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700",
                ].join(" ")}
              >
                {filter.label}
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

function TimeWindowTabs({
  value,
  onChange,
}: {
  value: TimeWindow;
  onChange: (value: TimeWindow) => void;
}) {
  return (
    <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5 text-sm">
      {TIME_WINDOWS.map((window) => (
        <button
          key={window.value}
          type="button"
          onClick={() => onChange(window.value)}
          aria-pressed={value === window.value}
          className={
            value === window.value
              ? "rounded bg-emerald-50 px-2.5 py-1 font-medium text-emerald-800"
              : "rounded px-2.5 py-1 text-slate-600 hover:text-slate-900"
          }
        >
          {window.label}
        </button>
      ))}
    </div>
  );
}

function CustomerReferenceViewTabs({
  value,
  onChange,
}: {
  value: CustomerReferenceView;
  onChange: (value: CustomerReferenceView) => void;
}) {
  const options: { value: CustomerReferenceView; label: string }[] = [
    { value: "grouped", label: "Gruppiert" },
    { value: "raw", label: "Rohreferenzen" },
  ];
  return (
    <div
      aria-label="Kundenansicht"
      className="inline-flex rounded-md border border-slate-200 bg-white p-0.5 text-sm"
    >
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(option.value)}
          aria-pressed={value === option.value}
          className={
            value === option.value
              ? "rounded bg-emerald-50 px-2.5 py-1 font-medium text-emerald-800"
              : "rounded px-2.5 py-1 text-slate-600 hover:text-slate-900"
          }
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function CoverageEntityTable({
  query,
  rows,
  loadingLabel,
  showTypeColumn,
  isCustomerAnchorView,
  hasLoadedOnce,
  onEdit,
}: {
  query: { isLoading: boolean; isError: boolean; isFetching: boolean };
  rows: CostCenterSourceCoverage[];
  loadingLabel: string;
  showTypeColumn: boolean;
  isCustomerAnchorView: boolean;
  hasLoadedOnce: boolean;
  onEdit: (row: CostCenterSourceCoverage) => void;
}) {
  const showLoadingRows = query.isLoading || query.isFetching;
  const loadingVerb = query.isLoading && !hasLoadedOnce ? "Lade" : "Aktualisiere";
  const columnCount = showTypeColumn ? 5 : 4;
  return (
    <section className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
      <div className="overflow-x-auto">
        <table
          className={`w-full ${
            showTypeColumn ? "min-w-[880px]" : "min-w-[820px]"
          } table-fixed divide-y divide-slate-200 text-sm`}
        >
          <colgroup>
            <col className={showTypeColumn ? "w-[31%]" : "w-[38%]"} />
            {showTypeColumn ? <col className="w-[15%]" /> : null}
            <col className="w-[16%]" />
            <col className="w-[12%]" />
            <col className={showTypeColumn ? "w-[17%]" : "w-[20%]"} />
          </colgroup>
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <TableHeadCell>{isCustomerAnchorView ? "Kunde" : "Kostenstelle"}</TableHeadCell>
              {showTypeColumn ? <TableHeadCell>Typ</TableHeadCell> : null}
              <TableHeadCell align="right">Ausgaben</TableHeadCell>
              <TableHeadCell align="right">Belege</TableHeadCell>
              <TableHeadCell align="right">Letzter Beleg</TableHeadCell>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {showLoadingRows && (
              <TableLoadingRow
                colSpan={columnCount}
                label={`${loadingVerb} "${loadingLabel}"...`}
              />
            )}
            {!showLoadingRows && query.isError && (
              <TableErrorRow colSpan={columnCount}>
                Kostenstellen konnten nicht geladen werden.
              </TableErrorRow>
            )}
            {!showLoadingRows && !query.isError && rows.length === 0 && (
              <TableEmptyRow colSpan={columnCount}>
                Keine Kostenstellen gefunden.
              </TableEmptyRow>
            )}
            {!showLoadingRows &&
              rows.map((row) => (
                <CoverageEntityRow
                  key={row.entity_key}
                  row={row}
                  showTypeColumn={showTypeColumn}
                  isCustomerAnchorView={isCustomerAnchorView}
                  onEdit={onEdit}
                />
              ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

const ACTIVITY_PAGE_STEP = 20;

const MANUAL_EVENT_FILTERS: { value: string; label: string }[] = [
  { value: "all", label: "Alle Arten" },
  { value: "display_name", label: "Name" },
  { value: "cost_center_grouping", label: "Zuordnung" },
  { value: "relationship_decision", label: "Auto-Match" },
  { value: "cost_center_alias", label: "Merge" },
  { value: "manual_entity", label: "Kostenstelle" },
];

function ManualChangesPanel({
  query,
  onJump,
}: {
  query: ManualEventsPanelQuery;
  onJump: (event: CostCenterManualEvent) => void;
}) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [kindFilter, setKindFilter] = useState<string>("all");
  const [visibleCount, setVisibleCount] = useState(ACTIVITY_PAGE_STEP);
  const allEvents = query.data?.items ?? [];
  const undoMutation = useMutation({
    mutationFn: undoManualEvent,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["cost-center-manual-events"],
      });
      void queryClient.invalidateQueries({
        queryKey: ["cost-center-source-coverage"],
      });
      void queryClient.invalidateQueries({ queryKey: ["cost-center-members"] });
      void queryClient.invalidateQueries({
        queryKey: ["cost-center-assignments"],
      });
      void queryClient.invalidateQueries({
        queryKey: ["cost-center-relationships"],
      });
      void queryClient.invalidateQueries({ queryKey: ["cost-centers"] });
    },
  });
  const pendingUndoId = undoMutation.isPending
    ? undoMutation.variables?.event_id
    : null;

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return allEvents.filter((event) => {
      if (kindFilter !== "all" && event.event_kind !== kindFilter) return false;
      if (!needle) return true;
      return manualEventSearchText(event).includes(needle);
    });
  }, [allEvents, search, kindFilter]);

  // Reset the visible window whenever the active query changes.
  useEffect(() => {
    setVisibleCount(ACTIVITY_PAGE_STEP);
  }, [search, kindFilter]);

  const visible = filtered.slice(0, visibleCount);
  const groups = useMemo(() => groupManualEventsByDay(visible), [visible]);
  const remaining = filtered.length - visible.length;
  const isInitialLoading = query.isLoading && allEvents.length === 0;

  return (
    <section className="my-1">
      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="flex-1">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Aktivität durchsuchen…"
            ariaLabel="Aktivität durchsuchen"
          />
        </div>
        <FilterDropdown
          label={kindFilter === "all" ? "Alle Arten" : "Art"}
          ariaLabel="Nach Art filtern"
          options={MANUAL_EVENT_FILTERS}
          value={kindFilter}
          defaultValue="all"
          onChange={setKindFilter}
        />
      </div>

      {undoMutation.isError ? (
        <div className="mb-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
          {uiApiErrorMessage(
            undoMutation.error,
            "Rückgängig machen ist fehlgeschlagen.",
          )}
        </div>
      ) : null}

      {isInitialLoading ? (
        <div className="rounded-md border border-slate-200 bg-white px-3 py-6 text-center text-sm text-slate-500">
          Lädt manuelle Änderungen...
        </div>
      ) : query.isError ? (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          Manuelle Änderungen konnten nicht geladen werden.
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-md border border-slate-200 bg-white px-3 py-6 text-center text-sm text-slate-500">
          {allEvents.length === 0
            ? "Keine manuellen Änderungen gefunden."
            : "Keine Treffer für diese Suche."}
        </div>
      ) : (
        <div className="space-y-5">
          {groups.map((group) => (
            <div key={group.key}>
              <div className="sticky top-0 z-10 bg-white/95 pb-1.5 pt-0.5 text-xs font-semibold uppercase tracking-wide text-slate-400 backdrop-blur">
                {group.label}
              </div>
              <ol className="divide-y divide-slate-100 overflow-hidden rounded-lg border border-slate-200 bg-white">
                {group.events.map((event) => (
                  <ManualChangeTimelineItem
                    key={`${event.event_kind}:${event.event_id}`}
                    event={event}
                    onJump={onJump}
                    onUndo={() => undoMutation.mutate(event)}
                    isUndoing={pendingUndoId === event.event_id}
                  />
                ))}
              </ol>
            </div>
          ))}
          {remaining > 0 ? (
            <button
              type="button"
              onClick={() => setVisibleCount((count) => count + ACTIVITY_PAGE_STEP)}
              className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
            >
              Mehr laden ({formatCount(remaining)})
            </button>
          ) : (
            <p className="pb-1 text-center text-xs text-slate-400">
              Alle {formatCount(filtered.length)} Einträge angezeigt.
            </p>
          )}
        </div>
      )}
    </section>
  );
}

function ManualChangeTimelineItem({
  event,
  onJump,
  onUndo,
  isUndoing,
}: {
  event: CostCenterManualEvent;
  onJump: (event: CostCenterManualEvent) => void;
  onUndo: () => void;
  isUndoing: boolean;
}) {
  const title =
    event.cost_center_key && event.cost_center_type
      ? displayCostCenterTitle(
          event.cost_center_type,
          event.cost_center_key,
          event.display_label,
        )
      : null;
  const action = manualEventActionLabel(event);
  const context = manualEventContextLabel(event);
  const absoluteTime = formatDateTimeShort(event.created_at);
  const badge = manualEventKindBadge(event.event_kind);
  const canJump = Boolean(event.cost_center_type && event.cost_center_key);
  const canUndo = manualEventIsUndoable(event);
  const actor = manualEventActorLabel(event.actor_user_id);
  return (
    <li className="px-4 py-3">
      <div className="flex items-start gap-3">
        <span
          className={`mt-0.5 flex-none rounded px-1.5 py-0.5 text-[11px] font-medium ${badge.className}`}
        >
          {badge.label}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-sm font-medium text-slate-900">{action}</span>
            <time
              className="flex-none text-xs tabular-nums text-slate-400"
              title={absoluteTime}
            >
              {formatTimeShort(event.created_at)}
            </time>
          </div>
          {title ? (
            canJump ? (
              <button
                type="button"
                onClick={() => onJump(event)}
                className="mt-0.5 block max-w-full truncate text-left text-sm text-emerald-700 underline-offset-2 hover:underline"
                title={`${title} öffnen`}
              >
                {title}
              </button>
            ) : (
              <span className="mt-0.5 block truncate text-sm text-slate-500">
                {title}
              </span>
            )
          ) : null}
          {context && context !== "-" ? (
            <div className="mt-0.5 truncate text-xs text-slate-500" title={context}>
              {context}
            </div>
          ) : null}
          <div className="mt-1.5 flex items-center gap-3 text-xs text-slate-400">
            <span className="truncate" title={actor}>
              {actor}
            </span>
            {canUndo ? (
              <button
                type="button"
                onClick={onUndo}
                disabled={isUndoing}
                className="flex-none font-medium text-slate-500 hover:text-rose-700 disabled:cursor-default disabled:text-slate-300"
              >
                {isUndoing ? "Wird rückgängig gemacht…" : "Rückgängig"}
              </button>
            ) : null}
            {canJump ? (
              <button
                type="button"
                onClick={() => onJump(event)}
                className="flex-none font-medium text-slate-500 hover:text-emerald-700"
              >
                Öffnen
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </li>
  );
}

function manualEventSearchText(event: CostCenterManualEvent): string {
  return [
    manualEventActionLabel(event),
    manualEventKindBadge(event.event_kind).label,
    event.display_label,
    event.cost_center_key,
    event.target_display_label,
    event.note,
    manualEventActorLabel(event.actor_user_id),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

type ManualEventDayGroup = {
  key: string;
  label: string;
  events: CostCenterManualEvent[];
};

function groupManualEventsByDay(
  events: CostCenterManualEvent[],
): ManualEventDayGroup[] {
  const groups: ManualEventDayGroup[] = [];
  const indexByKey = new Map<string, number>();
  for (const event of events) {
    const key = manualEventDayKey(event.created_at);
    let index = indexByKey.get(key);
    if (index === undefined) {
      index = groups.length;
      indexByKey.set(key, index);
      groups.push({
        key,
        label: manualEventDayLabel(event.created_at),
        events: [],
      });
    }
    groups[index].events.push(event);
  }
  return groups;
}

function manualEventDayKey(value: string | null | undefined): string {
  if (!value) return "unknown";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "unknown";
  return new Intl.DateTimeFormat("en-CA", {
    day: "2-digit",
    month: "2-digit",
    timeZone: "Europe/Vienna",
    year: "numeric",
  }).format(date);
}

function manualEventDayLabel(value: string | null | undefined): string {
  if (!value) return "Unbekannt";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Unbekannt";
  const key = manualEventDayKey(value);
  const todayKey = manualEventDayKey(new Date().toISOString());
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  if (key === todayKey) return "Heute";
  if (key === manualEventDayKey(yesterday.toISOString())) return "Gestern";
  return new Intl.DateTimeFormat("de-AT", {
    day: "2-digit",
    month: "long",
    timeZone: "Europe/Vienna",
    weekday: "long",
    year: "numeric",
  }).format(date);
}

function formatTimeShort(value: string | null | undefined): string {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("de-AT", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Vienna",
  }).format(date);
}

const MANUAL_EVENT_BADGES: Record<string, { label: string; className: string }> = {
  display_name: { label: "Name", className: "bg-sky-100 text-sky-700" },
  cost_center_grouping: {
    label: "Zuordnung",
    className: "bg-emerald-100 text-emerald-700",
  },
  relationship_decision: {
    label: "Auto-Match",
    className: "bg-amber-100 text-amber-700",
  },
  cost_center_alias: { label: "Merge", className: "bg-violet-100 text-violet-700" },
  manual_entity: { label: "Kostenstelle", className: "bg-slate-100 text-slate-600" },
};

function manualEventKindBadge(kind: string): {
  label: string;
  className: string;
} {
  return (
    MANUAL_EVENT_BADGES[kind] ?? {
      label: "Änderung",
      className: "bg-slate-100 text-slate-600",
    }
  );
}

// Undo is offered only for events with a clean, source-safe inverse mutation.
// Manual-entity lifecycle (create/archive/role) and auto-match decisions are
// intentionally excluded; correct those in the cost-center drawer instead.
function manualEventIsUndoable(event: CostCenterManualEvent): boolean {
  if (event.event_kind === "display_name") {
    return event.action === "set" || event.action === "clear";
  }
  if (event.event_kind === "cost_center_grouping") {
    return event.action === "add" || event.action === "remove";
  }
  if (event.event_kind === "cost_center_alias") {
    return event.action === "merge" || event.action === "undo";
  }
  return false;
}

async function undoManualEvent(event: CostCenterManualEvent): Promise<unknown> {
  const note = "Rückgängig gemacht über Aktivität";
  if (event.event_kind === "display_name") {
    if (!event.cost_center_type || !event.cost_center_key) {
      throw new Error("Kostenstelle fehlt für Rückgängig.");
    }
    const costCenterType = event.cost_center_type as CostCenterType;
    if (event.previous_display_name) {
      return setCostCenterDisplayName({
        costCenterType,
        costCenterKey: event.cost_center_key,
        displayName: event.previous_display_name,
        note,
      });
    }
    return clearCostCenterDisplayName({
      costCenterType,
      costCenterKey: event.cost_center_key,
      note,
    });
  }
  if (event.event_kind === "cost_center_grouping") {
    // The event row stores the member as cost_center_* and the parent as
    // target_*; undo flips the add/remove against that parent.
    if (
      !event.cost_center_type ||
      !event.cost_center_key ||
      !event.target_cost_center_type ||
      !event.target_cost_center_key
    ) {
      throw new Error("Zuordnung unvollständig für Rückgängig.");
    }
    const args = {
      parentCostCenterType: event.target_cost_center_type,
      parentCostCenterKey: event.target_cost_center_key,
      memberCostCenterType: event.cost_center_type,
      memberCostCenterKey: event.cost_center_key,
      note,
    };
    return event.action === "add"
      ? removeCostCenterMember(args)
      : addCostCenterMember(args);
  }
  if (event.event_kind === "cost_center_alias") {
    // cost_center_* is the source, target_* the merge target.
    if (
      !event.cost_center_type ||
      !event.cost_center_key ||
      !event.target_cost_center_type ||
      !event.target_cost_center_key
    ) {
      throw new Error("Zusammenführung unvollständig für Rückgängig.");
    }
    const args = {
      sourceCostCenterType: event.cost_center_type,
      sourceCostCenterKey: event.cost_center_key,
      targetCostCenterType: event.target_cost_center_type,
      targetCostCenterKey: event.target_cost_center_key,
      note,
    };
    return event.action === "undo"
      ? mergeCostCenterAlias(args)
      : undoCostCenterAlias(args);
  }
  throw new Error("Diese Änderung kann nicht automatisch rückgängig gemacht werden.");
}

function manualEventActionLabel(event: CostCenterManualEvent): string {
  if (event.event_kind === "display_name") {
    return event.action === "clear" ? "Name zurückgesetzt" : "Name geändert";
  }
  if (event.event_kind === "cost_center_grouping") {
    if (event.action === "remove") return "Zuordnung entfernt";
    if (event.action === "add") return "Kostenstelle zugeordnet";
    return "Zuordnung geändert";
  }
  if (event.event_kind === "cost_center_alias") {
    return event.action === "undo"
      ? "Zusammenführung aufgehoben"
      : "Kostenstellen zusammengeführt";
  }
  if (event.event_kind === "manual_entity") {
    if (event.action === "update_role") return "Projektrolle geändert";
    if (event.action === "archive") return "Projekt archiviert";
    return "Kostenstelle angelegt";
  }
  if (event.event_kind === "relationship_decision") {
    if (event.action === "create") return "Zuordnung angelegt";
    if (event.action === "remove") return "Zuordnung entfernt";
    if (event.action === "confirm") return "Zuordnung bestätigt";
    if (event.action === "reject") return "Zuordnung abgelehnt";
    if (event.action === "reset") return "Zuordnung zurückgesetzt";
  }
  return fallbackManualEventActionLabel(event.action);
}

function manualEventContextLabel(event: CostCenterManualEvent): string {
  if (event.event_kind === "display_name") {
    const previous = event.previous_display_name || "-";
    const next = event.new_display_name || "-";
    return `${previous} -> ${next}`;
  }
  if (event.event_kind === "cost_center_grouping") {
    const target = targetCostCenterEventLabel(event);
    return target ? `Übergeordnet: ${target}` : "Kostenstellen-Zuordnung";
  }
  if (event.event_kind === "cost_center_alias") {
    const target = targetCostCenterEventLabel(event) || "-";
    return event.action === "undo" ? `Ziel war ${target}` : `Ziel: ${target}`;
  }
  if (event.event_kind === "manual_entity") {
    return event.display_label || "Manuelle Kostenstelle";
  }
  if (event.event_kind === "relationship_decision") {
    const previous = event.previous_display_name || "-";
    const next = event.new_display_name || "-";
    return `${previous} -> ${next}`;
  }
  return "-";
}

function fallbackManualEventActionLabel(action: string): string {
  const labels: Record<string, string> = {
    add: "Hinzugefügt",
    archive: "Archiviert",
    assign: "Zugeordnet",
    clear: "Zurückgesetzt",
    create: "Angelegt",
    remove: "Entfernt",
    reset: "Zurückgesetzt",
    set: "Geändert",
    undo: "Rückgängig gemacht",
    update: "Geändert",
    update_role: "Projektrolle geändert",
  };
  return labels[action] ?? "Manuelle Änderung";
}

function targetCostCenterEventLabel(event: CostCenterManualEvent): string | null {
  if (!event.target_cost_center_type || !event.target_cost_center_key) return null;
  return displayCostCenterTitle(
    event.target_cost_center_type,
    event.target_cost_center_key,
    event.target_display_label,
  );
}

type ManualGroupingCreateVariables = {
  parent: CostCenterEntity;
  child: CostCenterEntity;
  reason?: string;
};

function ManualGroupingDrawer({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  useEscapeKey(onClose, open);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50">
      <button
        type="button"
        className="absolute inset-0 cursor-default bg-slate-900/20"
        aria-label="Zuordnung schließen"
        onClick={onClose}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby="manual-grouping-title"
        className="absolute inset-y-0 right-0 flex w-[min(40rem,100vw)] flex-col border-l border-slate-200 bg-white shadow-2xl"
      >
        <header className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Manuelle Zuordnung
            </div>
            <h2
              id="manual-grouping-title"
              className="mt-1 text-lg font-semibold text-slate-900"
            >
              Zuordnung anlegen
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Eine bestehende Kostenstelle einer anderen zuordnen. Es werden nur
              bestehende Kostenstellen verknüpft.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="h-8 rounded-md px-2 text-sm text-slate-500 hover:bg-slate-100 hover:text-slate-900"
          >
            Schließen
          </button>
        </header>
        <div className="min-h-0 flex-1 overflow-auto px-5 py-4">
          <ManualGroupingForm onCreated={onCreated} />
        </div>
      </aside>
    </div>
  );
}

function ManualGroupingForm({ onCreated }: { onCreated: () => void }) {
  const queryClient = useQueryClient();
  const [parentSearch, setParentSearch] = useState("");
  const [childSearch, setChildSearch] = useState("");
  const [parent, setParent] = useState<CostCenterEntity | null>(null);
  const [child, setChild] = useState<CostCenterEntity | null>(null);
  const [reason, setReason] = useState("");
  const [successLabel, setSuccessLabel] = useState<string | null>(null);
  const parentQueryText = useDebouncedValue(parentSearch, 200);
  const childQueryText = useDebouncedValue(childSearch, 200);
  const parentSearchIsDebouncing = parentSearch.trim() !== parentQueryText.trim();
  const childSearchIsDebouncing = childSearch.trim() !== childQueryText.trim();
  const parentQuery = useQuery({
    queryKey: ["cost-centers", "manual-grouping-parent", parentQueryText],
    queryFn: () =>
      fetchCostCenters({
        q: parentQueryText,
        sort: "name_asc",
        limit: 8,
      }),
    enabled: parentQueryText.trim().length >= 2,
    staleTime: 30_000,
  });
  const childQuery = useQuery({
    queryKey: ["cost-centers", "manual-grouping-child", childQueryText],
    queryFn: () =>
      fetchCostCenters({
        q: childQueryText,
        sort: "name_asc",
        limit: 8,
      }),
    enabled: childQueryText.trim().length >= 2,
    staleTime: 30_000,
  });
  const createMutation = useMutation({
    mutationFn: ({ parent, child, reason }: ManualGroupingCreateVariables) =>
      addCostCenterMember({
        parentCostCenterType: parent.cost_center_type,
        parentCostCenterKey: parent.cost_center_key,
        memberCostCenterType: child.cost_center_type,
        memberCostCenterKey: child.cost_center_key,
        note: reason,
    }),
    onSuccess: (_result, variables) => {
      setSuccessLabel(
        manualGroupingSuccessLabel(variables.parent, variables.child),
      );
      setReason("");
      setParent(null);
      setChild(null);
      setParentSearch("");
      setChildSearch("");
      void queryClient.invalidateQueries({
        queryKey: ["cost-center-relationships"],
      });
      void queryClient.invalidateQueries({
        queryKey: ["cost-center-assignments"],
      });
      void queryClient.invalidateQueries({
        queryKey: ["cost-center-members"],
      });
      onCreated();
    },
  });
  const canSubmit =
    parent !== null &&
    child !== null &&
    !(
      parent.cost_center_type === child.cost_center_type &&
      parent.cost_center_key === child.cost_center_key
    ) &&
    !createMutation.isPending;

  return (
    <div>
      <div className="grid gap-3 lg:grid-cols-2">
        <ManualEntityPicker
          label="Übergeordnete Kostenstelle"
          value={parentSearch}
          selectedEntity={parent}
          placeholder="Übergeordnete Kostenstelle suchen…"
          isSearchPending={parentSearchIsDebouncing}
          query={parentQuery}
          onSearch={(value) => {
            setParentSearch(value);
            setParent(null);
            setSuccessLabel(null);
          }}
          onSelect={(entity) => {
            setParent(entity);
            setParentSearch(manualEntityTitle(entity));
          }}
        />
        <ManualEntityPicker
          label="Zugeordnete Kostenstelle"
          value={childSearch}
          selectedEntity={child}
          placeholder="Zugeordnete Kostenstelle suchen…"
          isSearchPending={childSearchIsDebouncing}
          query={childQuery}
          onSearch={(value) => {
            setChildSearch(value);
            setChild(null);
            setSuccessLabel(null);
          }}
          onSelect={(entity) => {
            setChild(entity);
            setChildSearch(manualEntityTitle(entity));
          }}
        />
      </div>
      <div className="mt-3 flex flex-wrap items-end gap-3">
        <label className="min-w-[16rem] flex-1 text-xs font-medium uppercase tracking-wide text-slate-500">
          Grund optional
          <input
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="z. B. vom Kunden bestätigt"
            className="mt-1 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm font-normal normal-case tracking-normal text-slate-800 outline-none placeholder:text-slate-400 focus:border-emerald-300 focus:ring-2 focus:ring-emerald-500/20"
          />
        </label>
        <button
          type="button"
          disabled={!canSubmit}
          onClick={() => {
            if (!parent || !child) return;
            createMutation.mutate({
              parent,
              child,
              reason: reason.trim() || undefined,
            });
          }}
          className="h-10 rounded-md border border-emerald-200 bg-emerald-50 px-3 text-sm font-medium text-emerald-800 hover:bg-emerald-100 disabled:cursor-default disabled:border-slate-200 disabled:bg-white disabled:text-slate-400"
        >
          {createMutation.isPending ? "Speichert..." : "Anlegen"}
        </button>
      </div>
      <div className="mt-2 min-h-5 text-xs">
        {createMutation.isError ? (
          <span className="text-rose-600">
            {uiApiErrorMessage(
              createMutation.error,
              "Manuelle Zuordnung konnte nicht angelegt werden.",
            )}
          </span>
        ) : successLabel ? (
          <span className="text-emerald-700">{successLabel}</span>
        ) : (
          <span className="text-slate-500">
            Es werden nur bestehende Kostenstellen verknüpft.
          </span>
        )}
      </div>
    </div>
  );
}

function ManualEntityPicker({
  label,
  value,
  selectedEntity,
  placeholder,
  disabled = false,
  isSearchPending = false,
  query,
  onSearch,
  onSelect,
}: {
  label: string;
  value: string;
  selectedEntity: CostCenterEntity | null;
  placeholder: string;
  disabled?: boolean;
  isSearchPending?: boolean;
  query: {
    isFetching: boolean;
    data?: { items: CostCenterEntity[] };
  };
  onSearch: (value: string) => void;
  onSelect: (entity: CostCenterEntity) => void;
}) {
  const showResults = !disabled && value.trim().length >= 2 && !selectedEntity;
  return (
    <div>
      <label className="text-xs font-medium uppercase tracking-wide text-slate-500">
        {label}
        <input
          value={value}
          onChange={(event) => onSearch(event.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          className="mt-1 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm font-normal normal-case tracking-normal text-slate-800 outline-none placeholder:text-slate-400 focus:border-emerald-300 focus:ring-2 focus:ring-emerald-500/20 disabled:bg-slate-50 disabled:text-slate-400"
        />
      </label>
      {selectedEntity ? (
        <div className="mt-1 text-xs text-emerald-700">
          Ausgewählt: {manualEntityTitle(selectedEntity)}
        </div>
      ) : showResults ? (
        <div className="mt-1 max-h-48 overflow-y-auto rounded-md border border-slate-200 bg-white shadow-sm">
          {isSearchPending || query.isFetching ? (
            <div className="px-3 py-2 text-xs text-slate-500">Sucht...</div>
          ) : query.data?.items.length ? (
            query.data.items.map((entity) => (
              <button
                key={entity.cost_center_key}
                type="button"
                onClick={() => onSelect(entity)}
                className="block w-full px-3 py-2 text-left text-sm hover:bg-slate-50"
              >
                <span className="block font-medium text-slate-900">
                  {manualEntityTitle(entity)}
                </span>
                <span className="block truncate text-xs text-slate-500">
                  {manualEntityMeta(entity)}
                </span>
              </button>
            ))
          ) : (
            <div className="px-3 py-2 text-xs text-slate-500">
              Keine Treffer.
            </div>
          )}
        </div>
      ) : (
        <div className="mt-1 text-xs text-slate-400">Mindestens 2 Zeichen.</div>
      )}
    </div>
  );
}

function manualEntityTitle(entity: CostCenterEntity): string {
  return displayCostCenterTitle(
    entity.cost_center_type,
    entity.cost_center_key,
    entity.display_label,
    {
      customerAnchorLabel: entity.customer_anchor_label,
      manualLabel: entity.manual_display_label,
    },
  );
}

function manualEntityRawReference(entity: CostCenterEntity): string | null {
  return displayCostCenterRawReference(
    entity.cost_center_type,
    entity.cost_center_key,
    entity.source_display_label ?? entity.display_label,
  );
}

function manualEntityMeta(entity: CostCenterEntity): string {
  const rawReference = manualEntityRawReference(entity);
  if (rawReference && rawReference !== manualEntityTitle(entity)) {
    return `Kundenreferenz · ${rawReference}`;
  }
  return (
    displayCostCenterMeta(
      entity.cost_center_type,
      entity.cost_center_key,
      manualEntityTitle(entity),
    ) ?? costCenterTypeLabel(entity.cost_center_type)
  );
}

function manualGroupingSuccessLabel(
  parentEntity: CostCenterEntity,
  childEntity: CostCenterEntity,
): string {
  const parent = displayCostCenterTitle(
    parentEntity.cost_center_type,
    parentEntity.cost_center_key,
    parentEntity.display_label,
    {
      customerAnchorLabel: parentEntity.customer_anchor_label,
      manualLabel: parentEntity.manual_display_label,
    },
  );
  const child = displayCostCenterTitle(
    childEntity.cost_center_type,
    childEntity.cost_center_key,
    childEntity.display_label,
    {
      customerAnchorLabel: childEntity.customer_anchor_label,
      manualLabel: childEntity.manual_display_label,
    },
  );
  return `Manuelle Zuordnung angelegt: ${parent} -> ${child}`;
}

function CoverageEntityRow({
  row,
  showTypeColumn,
  isCustomerAnchorView,
  onEdit,
}: {
  row: CostCenterSourceCoverage;
  showTypeColumn: boolean;
  isCustomerAnchorView: boolean;
  onEdit: (row: CostCenterSourceCoverage) => void;
}) {
  const location = useLocation();
  const originRoute = currentRoute(location);
  const [showRawReferences, setShowRawReferences] = useState(false);
  const rawReferenceCount = toNumber(row.raw_reference_count);
  const rawReferences = row.raw_references ?? [];
  const shownRawReferenceCount = rawReferences.length;
  const hasMoreRawReferences = row.raw_reference_has_more === true;
  const hasRawReferenceDrilldown =
    isCustomerAnchorView &&
    row.entity_type === "customer_reference" &&
    row.row_kind === "customer_anchor" &&
    rawReferenceCount > 0;
  const href = hasRawReferenceDrilldown
    ? null
    : periodDetailHref(costCenterHref(row.entity_type, row.entity_key), originRoute);
  const columnCount = showTypeColumn ? 5 : 4;
  const creditSpend = creditSpendLabel(row.credit_spend);
  return (
    <Fragment>
      <tr className="hover:bg-slate-50">
        <td className="px-3 py-2">
          <CostCenterTitle
            type={row.entity_type}
            costCenterKey={row.entity_key}
            label={row.display_label}
            sourceLabel={row.source_display_label}
            manualLabel={row.manual_display_label}
            customerAnchorLabel={row.customer_anchor_label}
            customerAnchorReason={row.customer_anchor_reason}
            href={href}
            linkState={{ from: originRoute }}
            onEdit={
              hasRawReferenceDrilldown || isDemoMode()
                ? undefined
                : () => onEdit(row)
            }
          />
          {hasRawReferenceDrilldown ? (
            <button
              type="button"
              onClick={() => setShowRawReferences((current) => !current)}
              className="mt-1 text-xs font-medium text-emerald-700 hover:text-emerald-900"
              aria-expanded={showRawReferences}
            >
              {showRawReferences ? "Referenzen ausblenden" : "Referenzen anzeigen"} ·{" "}
              {hasMoreRawReferences
                ? `erste ${formatCount(shownRawReferenceCount)} von ${formatCount(rawReferenceCount)}`
                : formatCount(rawReferenceCount)}{" "}
              {rawReferenceCount === 1 ? "Referenz" : "Referenzen"}
            </button>
          ) : null}
        </td>
        {showTypeColumn ? (
          <td
            className="truncate px-3 py-2 text-slate-700"
            title={costCenterTypeLabel(row.entity_type)}
          >
            {costCenterTypeLabel(row.entity_type)}
          </td>
        ) : null}
        <td className="px-3 py-2 text-right tabular-nums text-slate-900">
          {/* Gutschriften nur noch als Hinweis beim Darüberfahren (#630). */}
          <span title={creditSpend ?? undefined}>{formatEuro(row.total_spend)}</span>
        </td>
        <td className="px-3 py-2 text-right tabular-nums text-slate-700">
          {formatCount(row.pdf_document_count)}
        </td>
        <td className="px-3 py-2 text-right tabular-nums text-slate-700">
          {formatDateShort(row.last_observed_date)}
        </td>
      </tr>
      {showRawReferences ? (
        <tr className="bg-slate-50/60">
          <td colSpan={columnCount} className="px-3 py-3">
            <CustomerAnchorRawReferences
              references={rawReferences}
              originRoute={originRoute}
            />
          </td>
        </tr>
      ) : null}
    </Fragment>
  );
}

function CustomerAnchorRawReferences({
  references,
  originRoute,
}: {
  references: CostCenterAnchorRawReference[];
  originRoute: string;
}) {
  if (references.length === 0) {
    return (
      <div className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-500">
        Keine Rohreferenzen geladen.
      </div>
    );
  }
  return (
    <div className="rounded-md border border-slate-200 bg-white">
      <div className="grid grid-cols-[minmax(0,1fr)_7rem_6rem_6rem] gap-3 border-b border-slate-100 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
        <div>Rohreferenz</div>
        <div className="text-right">Ausgaben</div>
        <div className="text-right">Belege</div>
        <div className="text-right">Letzter Beleg</div>
      </div>
      <div className="max-h-72 overflow-auto divide-y divide-slate-100">
        {references.map((reference) => {
          const creditSpend = creditSpendLabel(reference.credit_spend);
          const title =
            displayCostCenterRawReference(
              "customer_reference",
              reference.entity_key,
              reference.source_display_label ?? reference.display_label,
            ) ?? reference.display_label ?? reference.entity_key;
          const href = periodDetailHref(costCenterHref("customer_reference", reference.entity_key), originRoute);
          return (
            <div
              key={reference.entity_key}
              className="grid grid-cols-[minmax(0,1fr)_7rem_6rem_6rem] gap-3 px-3 py-2 text-sm"
            >
              <div className="min-w-0">
                {href ? (
                  <Link
                    to={href}
                    state={{ from: originRoute }}
                    className="block truncate font-medium text-slate-900 hover:text-emerald-800"
                    title={title}
                  >
                    {title}
                  </Link>
                ) : (
                  <div className="truncate font-medium text-slate-900" title={title}>
                    {title}
                  </div>
                )}
                {reference.manual_display_label &&
                reference.source_display_label &&
                reference.manual_display_label !== reference.source_display_label ? (
                  <div className="mt-0.5 truncate text-xs text-slate-500">
                    Quelle: {reference.source_display_label}
                  </div>
                ) : null}
              </div>
              <div className="text-right tabular-nums text-slate-900">
                <div>{formatEuro(reference.total_spend)}</div>
                {creditSpend ? (
                  <div className="mt-0.5 text-xs font-normal text-amber-700">
                    {creditSpend}
                  </div>
                ) : null}
              </div>
              <div className="text-right tabular-nums text-slate-700">
                {formatCount(reference.pdf_document_count ?? reference.document_count)}
              </div>
              <div className="text-right tabular-nums text-slate-700">
                {formatDateShort(reference.last_observed_date)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CostCenterTitle({
  type,
  costCenterKey,
  label,
  sourceLabel,
  manualLabel,
  customerAnchorLabel,
  href,
  linkState,
  onEdit,
}: {
  type: string;
  costCenterKey: string;
  label: string | null;
  sourceLabel?: string | null;
  manualLabel?: string | null;
  customerAnchorLabel?: string | null;
  customerAnchorReason?: string | null;
  href: string | null;
  linkState?: unknown;
  onEdit?: () => void;
}) {
  const title = displayCostCenterTitle(type, costCenterKey, label, {
    customerAnchorLabel,
    manualLabel,
  });
  const meta = displayCostCenterMeta(type, costCenterKey, title);
  const rawReference = customerAnchorLabel
    ? displayCostCenterRawReference(type, costCenterKey, sourceLabel ?? label)
    : null;
  const titleBlock = (
    <>
      <div className="truncate font-medium text-slate-900" title={title}>
        {title}
      </div>
      {rawReference && rawReference !== title ? (
        <div className="mt-0.5 truncate text-xs text-slate-500" title={rawReference}>
          Referenz: {rawReference}
        </div>
      ) : meta ? (
        <div className="mt-0.5 truncate text-xs text-slate-500">{meta}</div>
      ) : null}
      {!rawReference && manualLabel && sourceLabel && manualLabel !== sourceLabel ? (
        <div className="mt-0.5 truncate text-xs text-slate-500" title={sourceLabel}>
          Quelle: {sourceLabel}
        </div>
      ) : null}
    </>
  );
  const titleContent = href ? (
    <Link to={href} state={linkState} className="block" title={costCenterKey}>
      {titleBlock}
    </Link>
  ) : (
    titleBlock
  );
  return (
    <div className="min-w-0">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">{titleContent}</div>
        {onEdit ? (
          <button
            type="button"
            onClick={onEdit}
            aria-label={`${title} bearbeiten`}
            title="Kostenstelle bearbeiten"
            className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-transparent text-slate-600 transition hover:border-slate-200 hover:bg-white hover:text-slate-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-500"
          >
            <PencilIcon />
          </button>
        ) : null}
      </div>
    </div>
  );
}

function PencilIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-3.5 w-3.5"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    >
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  );
}

function currentRoute(location: {
  pathname: string;
  search: string;
  hash: string;
}): string {
  return `${location.pathname}${location.search}${location.hash}`;
}

function creditSpendLabel(value: string | number | null | undefined): string | null {
  const amount = toNumber(value);
  if (amount === 0) return null;
  return `davon Gutschriften ${formatEuro(amount)}`;
}

function toNumber(value: string | number | null | undefined): number {
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

function typeFilterLoadingLabel(value: TypeFilter): string {
  return TYPE_FILTERS.find((filter) => filter.value === value)?.label ?? "Kostenstellen";
}

function formatCount(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return "–";
  return formatQty(value);
}

function periodDetailHref(href: string | null, origin: string): string | null {
  if (!href) return null;
  const target = new URL(href, "http://local");
  target.searchParams.set("return_to", origin);
  target.searchParams.set("window", new URL(origin, "http://local").searchParams.get("window") ?? "last_year");
  return target.pathname + target.search;
}
